import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline'
import { map } from 'lodash'
import { ReactNode } from 'react'
import { useQuery } from 'react-query'

import { UserResponse } from '@/components/User'
import { getUserById } from '@/components/User/basicQueries'
import cn from '@/utilities/cn'

import Image from './Image'

type AvatarStackProps = {
  userIds: string[]
  className?: string
  maxLength?: number
  isOverflowHidden?: boolean
}
/**
 * A stack of avatar icons, layered from top to bottom.
 * Maximum of 4 avatars.
 */
export const AvatarStackTopToBottom = ({
  userIds,
  className,
  maxLength = 4,
  isOverflowHidden = true
}: AvatarStackProps): ReactNode => {
  return (
    <div className={cn(`isolate flex h-8 items-center -space-x-2 ${isOverflowHidden && 'overflow-hidden'}`, className)}>
      {map(userIds.slice(0, maxLength), (userId, index) => {
        const zIndex = (maxLength - 1) * 10 - index * 10
        return <UserAvatar key={userId} userId={userId} className={`z-${zIndex}`} />
      })}
      {userIds.length > maxLength && <EllipsisHorizontalIcon className="h-5 w-5 stroke-zinc-400" />}
    </div>
  )
}

type AvatarProps = {
  userId: string
  className: string
}
function UserAvatar({ userId, className }: AvatarProps) {
  const userQuery = useQuery<UserResponse>({ ...getUserById({ userId }), enabled: userId != null })

  const userPhoto =
    userQuery.data?.data?.photos?.find(photo => photo.primary)?.value ||
    'https://cdn.workgrid.com/templates/f8884411-8639-4d72-a6b6-76105f195011/profile.png'
  const givenName = userQuery.data?.data?.name.givenName
  const familyName = userQuery.data?.data?.name.familyName
  const displayName = givenName && familyName ? `${givenName} ${familyName}` : 'Owned By'

  return (
    <Image
      loadingImage={<EmptyProfilePhoto title={displayName} className={className} />}
      fallbackImage={<EmptyProfilePhoto title={displayName} className={className} />}
      className={cn('fade-in relative inline-block h-6 w-6 rounded-full bg-zinc-100 ring-1 ring-white', className)}
      src={userPhoto}
      alt="User Profile Icon"
      id={userId}
      title={displayName}
    />
  )
}
type EmptyProfilePhotoProps = {
  title: string
  className?: string
}
function EmptyProfilePhoto({ title, className }: EmptyProfilePhotoProps) {
  return (
    <img
      alt="Empty user profile icon"
      className={cn('fade-in relative inline-block h-6 w-6 rounded-full bg-zinc-100 ring-2 ring-white', className)}
      src="https://cdn.workgrid.com/templates/f8884411-8639-4d72-a6b6-76105f195011/profile.png"
      title={title}
    />
  )
}
